import { Button, ButtonProps, useDisclosure } from "@chakra-ui/react";
import React, { useCallback } from "react";
import { RiEditLine } from "react-icons/ri";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import { useEditFormTemplateContext } from "../../context";
import { SectionFormDrawer, SectionFormType } from "./SectionFormDrawer";

type AddSectionButtonProps = ButtonProps & {
  section: AF.V3.Section;
};
export const EditSectionButton: React.FC<AddSectionButtonProps> = ({
  section,
}) => {
  const { onOpen, onClose, isOpen } = useDisclosure();
  const { dispatch } = useEditFormTemplateContext();

  const handleOnUpdate = useCallback(
    (values: SectionFormType) => {
      if (!dispatch) {
        return;
      }

      let updatedSection: AF.V3.Section;
      switch (values.type) {
        case GQL.form_template_section_type_enum.PreRankingSection: {
          updatedSection = {
            ...section,
            id: section.id,
            type: "PreRankingSection",
            title: values.title,
            description: values.description,
            key: values.key,
            permissionLevel: undefined,
            familyEditableStatuses:
              values.familyEditableStatuses?.map((status) => status.id) ?? [],
            questions: "questions" in section ? section.questions ?? [] : [],
          } satisfies AF.V3.PreRankingSection;
          break;
        }

        case GQL.form_template_section_type_enum.SchoolRankingSection: {
          updatedSection = {
            ...section,
            id: section.id,
            type: "SchoolRankingSection",
            title: values.title,
            description: values.description,
            key: values.key,
            permissionLevel: undefined,
            familyEditableStatuses:
              values.familyEditableStatuses?.map((status) => status.id) ?? [],
            minSchools: values.minSchools
              ? Number(values.minSchools)
              : undefined,
            maxSchools: values.maxSchools
              ? Number(values.maxSchools)
              : undefined,
            exploreUrl: values.exploreUrl,
            rankingEnabled: values.rankingEnabled,
          } satisfies AF.V3.SchoolRankingSection;
          break;
        }

        case GQL.form_template_section_type_enum.GeneralSection: {
          updatedSection = {
            ...section,
            id: section.id,
            type: "GeneralSection",
            title: values.title,
            description: values.description,
            key: values.key,
            permissionLevel: values.permissionLevelSwitch
              ? (values.permissionLevel as AF.V3.PermissionLevel)
              : undefined,
            familyEditableStatuses:
              values.familyEditableStatuses?.map((status) => status.id) ?? [],
            questions: "questions" in section ? section.questions ?? [] : [],
          } satisfies AF.V3.GeneralSection;
          break;
        }

        case GQL.form_template_section_type_enum.DisclaimerSection: {
          updatedSection = {
            ...section,
            id: section.id,
            type: "DisclaimerSection",
            title: values.title,
            description: values.description,
            key: values.key,
            permissionLevel: values.permissionLevelSwitch
              ? (values.permissionLevel as AF.V3.PermissionLevel)
              : undefined,
            familyEditableStatuses:
              values.familyEditableStatuses?.map((status) => status.id) ?? [],
            disclaimer: "disclaimer" in section ? section.disclaimer : "",
          } satisfies AF.V3.DisclaimerSection;
          break;
        }

        default: {
          throw new Error(`Unhandled section type: ${values.type}`);
        }
      }

      dispatch({
        type: "UpdateSection",
        section: updatedSection,
      });

      onClose();
    },
    [dispatch, section, onClose]
  );

  return (
    <>
      <Button
        variant="outline"
        colorScheme="gray"
        leftIcon={<RiEditLine />}
        onClick={onOpen}
      >
        Edit section
      </Button>

      <SectionFormDrawer
        isOpen={isOpen}
        section={section}
        onCreate={handleOnUpdate}
        onCancelButtonClick={onClose}
      />
    </>
  );
};
